/**
 * 秒级计时引擎 — 复刻 mobile useTodayTotals 机制:每秒重算
 * computeAttendanceTotals(sessions, [], {closeAtMs: serverNowMs()}),
 * 开放段动态闭合,无 base+delta 插值;轮询/动作刷新 sessions 后自动校准。
 * curBreakSec / actSec 为独立口径(mobile useClockActions.currentBreakRunningTime 同款)。
 */
import { useEffect, useMemo, useState } from "react";
import { computeAttendanceTotals } from "@/utils/attendanceTotals";
import { serverNowMs } from "@/utils/serverClock";
import type { AttendanceSessionRef } from "@/types/attendance";

export interface ClockTick {
  /** 今日累计工作时长(attendance − break),开放段含到 now。 */
  workSec: number;
  /** 今日累计 break(含进行中的 break 到 now)。 */
  breakTodaySec: number;
  /** 本次 break 时长(非 break 时为 0)。 */
  curBreakSec: number;
  /** 运行中 special activity 时长(无则 0)。 */
  actSec: number;
}

export function useClockTicker(
  sessions: AttendanceSessionRef[],
  activityStartedAt: string | null,
): ClockTick {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  return useMemo<ClockTick>(() => {
    const now = serverNowMs();
    const totals = computeAttendanceTotals(sessions, [], { closeAtMs: now });

    let curBreakSec = 0;
    if (totals.hasOpenBreak) {
      for (let i = sessions.length - 1; i >= 0; i--) {
        if (sessions[i].sessionType === "break_start") {
          const startMs = Date.parse(sessions[i].actionTime);
          if (!Number.isNaN(startMs)) curBreakSec = Math.max(0, Math.floor((now - startMs) / 1000));
          break;
        }
      }
    }

    let actSec = 0;
    if (activityStartedAt) {
      const startMs = Date.parse(activityStartedAt);
      if (!Number.isNaN(startMs)) actSec = Math.max(0, Math.floor((now - startMs) / 1000));
    }

    return { workSec: totals.workSec, breakTodaySec: totals.breakSec, curBreakSec, actSec };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tick 驱动每秒重算(mobile useTodayTotals 同款机制)
  }, [sessions, activityStartedAt, tick]);
}
